import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Button,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from '../../config/axiosConfig';
import { useAuth } from '../AuthContext';

// Step components
import AlertTypeSelection from './steps/AlertTypeSelection';
import AlertConfigurationForm from './steps/AlertConfigurationForm';
import DeliveryChannelSelection from './steps/DeliveryChannelSelection';
import AlertSummary from './steps/AlertSummary';

// Icons
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';

import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

const steps = ['Select Alert Type', 'Configure Alert', 'Delivery Channels', 'Review & Create'];

const AlertCreationDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth(); // Get user data from auth hook
  const theme = useTheme();
  const matchesMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Get prefilled data if coming from another page (like an asset page)
  const prefilledData = location.state?.prefilledData || {};

  // Add state for limit checking
  const [limitReached, setLimitReached] = useState(false);
  const [checkingLimit, setCheckingLimit] = useState(true);

  const [activeStep, setActiveStep] = useState(0);
  const [alertData, setAlertData] = useState({
    name: '',
    type: prefilledData.type || '',
    triggerType: 'once',
    conditions: {},
    mainFilters: [],
    channels: {
      email: false,
      site: true,
      telegram: false,
      discord: false,
      whatsapp: false
    },
    ...prefilledData
  });

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [availableAlertTypes, setAvailableAlertTypes] = useState([]);

  // Use user data from auth hook to determine available alert types
  useEffect(() => {
    if (user && user.plan) {
      setAvailableAlertTypes(determineAvailableAlertTypes(user.plan.name));
    }
  }, [user]);

  // Jump to configuration step if type is prefilled
  useEffect(() => {
    if (prefilledData.type && activeStep === 0) {
      setActiveStep(1);
    }
  }, [prefilledData.type, activeStep]);

  // Check alert limit on mount
  useEffect(() => {
    checkAlertLimit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const checkAlertLimit = async () => {
    if (!user || !user.plan) {
      setCheckingLimit(false);
      return;
    }

    try {
      const response = await axios.get('/api/alerts');
      const alertCount = response.data.length;
      const alertLimit = user.plan.alertLimit || 5;

      // Check if limit is reached (-1 means unlimited)
      if (alertLimit !== -1 && alertCount >= alertLimit) {
        setLimitReached(true);
      }
    } catch (error) {
      console.error('Error checking alert limit:', error);
    } finally {
      setCheckingLimit(false);
    }
  };

  // Determine available alert types based on user plan
  const determineAvailableAlertTypes = (planName) => {
    // Default free tier has limited alert types
    const freeTierTypes = ['priceAlert', 'newsAlert'];

    // Basic tier adds congressional and insider trading
    const basicTierTypes = [...freeTierTypes, 'congressTrade', 'insiderTrade'];

    // Premium has all types
    const premiumTierTypes = [...basicTierTypes, 'marketEvent'];

    if (!planName) return freeTierTypes;

    // Check plan name (case-insensitive)
    const planNameLower = planName.toLowerCase();

    switch (planNameLower) {
      case 'premium':
        return premiumTierTypes;
      case 'basic':
        return basicTierTypes;
      default:
        return freeTierTypes;
    }
  };

  const handleNext = () => {
    if (activeStep === 0 && !alertData.type) {
      setErrorMessage('Please select an alert type');
      return;
    }

    if (activeStep === 1) {
      if (!validateConfigurationForm()) {
        return;
      }
    }

    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
    setAlertData({
      name: '',
      type: '',
      triggerType: 'continuous',
      conditions: {},
      mainFilters: [],
      channels: {
        email: true,
        site: true,
        telegram: false,
        discord: false,
        whatsapp: false
      }
    });
  };

  const validateConfigurationForm = () => {
    // Basic validation for alert name
    if (!alertData.name.trim()) {
      setErrorMessage('Alert name is required');
      return false;
    }

    // Type-specific validation
    switch (alertData.type) {
      case 'priceAlert':
        if (!alertData.mainFilters.some(f => f.type === 'symbol')) {
          setErrorMessage('Please select at least one stock symbol');
          return false;
        }
        if (
          !alertData.conditions.direction ||
          ((alertData.conditions.direction === 'above' || alertData.conditions.direction === 'below') && !alertData.conditions.price) ||
          ((alertData.conditions.direction === 'up' || alertData.conditions.direction === 'down') && !alertData.conditions.threshold)
        ) {
          setErrorMessage('Please complete all required price alert settings');
          return false;
        }
        break;

      case 'congressTrade':
        if (!alertData.mainFilters.length) {
          setErrorMessage('Please select at least one representative or stock symbol');
          return false;
        }
        break;

      case 'newsAlert':
        if (!alertData.mainFilters.length) {
          setErrorMessage('Please select at least one stock symbol or keyword');
          return false;
        }
        break;

      case 'insiderTrade':
        if (!alertData.mainFilters.some(f => f.type === 'symbol')) {
          setErrorMessage('Please select at least one stock symbol');
          return false;
        }
        break;

      case 'marketEvent':
        if (!alertData.conditions.eventTypes || alertData.conditions.eventTypes.length === 0) {
          setErrorMessage('Please select at least one event type');
          return false;
        }
        break;

      default:
        break;
    }

    return true;
  };

  const handleSubmit = async () => {
    setLoading(true);
    setErrorMessage('');

    try {
      await axios.post('/api/alerts', alertData);
      setSuccessMessage('Alert created successfully!');

      // Reset form after success
      setTimeout(() => {
        navigate('/alerts');
      }, 2000);
    } catch (error) {
      console.error('Error creating alert:', error);
      setErrorMessage(error.response?.data?.message || 'Error creating alert. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAlertData = (newData) => {
    setAlertData(prevData => ({
      ...prevData,
      ...newData
    }));
  };

  // Get the content for the current step
  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <AlertTypeSelection
            selectedType={alertData.type}
            onSelectType={(type) => handleUpdateAlertData({ type })}
            availableTypes={availableAlertTypes}
            userPlan={user?.plan}
          />
        );
      case 1:
        return (
          <AlertConfigurationForm
            alertData={alertData}
            onUpdateAlertData={handleUpdateAlertData}
          />
        );
      case 2:
        return (
          <DeliveryChannelSelection
            channels={alertData.channels}
            onUpdateChannels={(channels) => handleUpdateAlertData({ channels })}
            userPlan={user?.plan}
          />
        );
      case 3:
        return (
          <AlertSummary
            alertData={alertData}
          />
        );
      default:
        return 'Unknown step';
    }
  };

  // Show loading while checking limit
  if (checkingLimit) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Show limit reached message
  if (limitReached) {
    const planName = user?.plan?.name || 'Free';
    const alertLimit = user?.plan?.alertLimit || 5;

    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper
          elevation={0}
          variant="stats"
          sx={{
            p: 4,
            textAlign: 'center',
            borderRadius: 2
          }}
        >
          <WarningIcon
            sx={{
              fontSize: 60,
              color: 'warning.main',
              mb: 3
            }}
          />

          <Typography variant="h4" gutterBottom>
            Alert Limit Reached
          </Typography>

          <Typography variant="body1" color="text.secondary" paragraph>
            You've reached your {planName} plan limit of {alertLimit} alerts.
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            To create more alerts, you can either delete existing alerts or upgrade your plan.
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/alerts')}
            >
              Manage Alerts
            </Button>

            <Button
              variant="contained"
              color="primary"
              onClick={() => window.location.href = '/pricing'}
            >
              Upgrade Plan
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  return (
    <Container
      maxWidth="md"
      sx={{
        py: { xs: 2, md: 4 },
        px: { xs: 2, sm: 3, md: 4 }
      }}
    >
      <Paper
        elevation={0}
        variant="stats"
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          borderRadius: 2,
          mb: 4,
          overflowX: 'hidden'
        }}
      >
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
          Create New Alert
        </Typography>

        <Stepper
          activeStep={activeStep}
          sx={{
            pt: 3,
            pb: 5,
            display: { xs: 'none', sm: 'flex' }
          }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box
          sx={{
            display: { xs: 'flex', sm: 'none' },
            pt: 2,
            pb: 3,
            justifyContent: 'center'
          }}
        >
          <Typography variant="subtitle1" fontWeight="medium" align="center">
            Step {activeStep + 1}: {steps[activeStep]}
          </Typography>
        </Box>

        <Box>
          {activeStep === steps.length ? (
            <Box sx={{ textAlign: 'center' }}>
              <CheckCircleIcon color="success" sx={{ fontSize: 60, mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                Alert Created Successfully!
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                Your new alert has been set up and is now active.
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'center', gap: 2, mt: 4 }}>
                <Button
                  variant="outlined"
                  onClick={handleReset}
                  startIcon={<ArrowBackIcon />}
                  fullWidth={matchesMobile}
                >
                  Create Another Alert
                </Button>
                <Button
                  variant="contained"
                  onClick={() => navigate('/alerts')}
                  fullWidth={matchesMobile}
                >
                  View My Alerts
                </Button>
              </Box>
            </Box>
          ) : (
            <>
              <Box sx={{ mb: 4 }}>
                {getStepContent(activeStep)}
              </Box>

              <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: { xs: 'center', sm: 'space-between' },
                gap: 2,
                pt: 2
              }}>
                <Button
                  variant="outlined"
                  disabled={activeStep === 0}
                  onClick={handleBack}
                  startIcon={<ArrowBackIcon />}
                  fullWidth={matchesMobile}
                >
                  Back
                </Button>

                {activeStep === steps.length - 1 ? (
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={loading}
                    endIcon={loading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                    fullWidth={matchesMobile}
                  >
                    {loading ? 'Creating...' : 'Create Alert'}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    endIcon={<ArrowForwardIcon />}
                    fullWidth={matchesMobile}
                  >
                    Next
                  </Button>
                )}
              </Box>
            </>
          )}
        </Box>
      </Paper>

      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setErrorMessage('')} severity="error">
          {errorMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccessMessage('')} severity="success">
          {successMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AlertCreationDashboard;